import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext";
import { db } from "../firebaseConfig";
import { collection, addDoc, doc, updateDoc, deleteDoc } from "firebase/firestore";
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function Tasks() {
  const { tasks, users, currentUser, isMaster, loading } = useWebData();

  const currentSender = currentUser ? (currentUser.displayName || currentUser.email.split('@')[0]) : "Admin";

  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("month");
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  // States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  
  // New Task State
  const [newTask, setNewTask] = useState({ 
      from: currentSender, 
      to: "", 
      department: "", 
      task: "", 
      priority: "Medium", 
      dueDate: "" 
  });

  // 3. FILTER LOGIC
  useEffect(() => {
    // ✅ FIX 1: Added '|| []' to prevent crash if tasks is undefined
    let result = tasks || [];
    
    // Sort logic
    result = [...result].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    if (filterType === "month") result = result.filter(t => (t.createdAt || "").startsWith(selectedMonth));
    else if (filterType === "year") result = result.filter(t => (t.createdAt || "").startsWith(selectedYear));

    if (searchTerm) {
        const keywords = searchTerm.toLowerCase().split(" ").filter(w => w);
        result = result.filter(t => {
            const row = `${t.from} ${t.to} ${t.department} ${t.task} ${t.status} ${t.priority}`.toLowerCase();
            return keywords.every(w => row.includes(w));
        });
    }
    setFiltered(result);
  }, [tasks, searchTerm, filterType, selectedMonth, selectedYear]);

  // ... (Notification Logic remains same)
  const sendPushNotification = async (expoPushToken, taskTitle, fromName) => {
    if (!expoPushToken) return;
    const message = {
      to: expoPushToken,
      sound: 'default',
      title: 'New Task Assigned! 📋',
      body: `${fromName} assigned: ${taskTitle}`,
      data: { someData: 'goes here' },
    };
    try {
        fetch('https://exp.host/--/api/v2/push/send', {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(message),
        });
    } catch (error) { console.warn("Notification error:", error); }
  };

  const handleAddTask = async () => {
      if(!newTask.to || !newTask.task || !newTask.dueDate) return alert("Fill all details");
      
      try {
          await addDoc(collection(db, "tasks"), {
              ...newTask, 
              status: "Pending", 
              date: newTask.dueDate, 
              createdAt: new Date().toISOString(), 
              completionNote: "",
              completionDate: null
          });

          // ✅ FIX 2: Added '|| []' for users search
          const targetUser = (users || []).find(u => u.name === newTask.to);
          if (targetUser && targetUser.pushToken) {
              sendPushNotification(targetUser.pushToken, newTask.task, newTask.from);
          }

          alert("Task Assigned! 🎯");
          setNewTask({ from: currentSender, to: "", department: "", task: "", priority: "Medium", dueDate: "" });
          setShowAddTask(false);
          
      } catch (error) {
          console.error("Error adding task:", error);
          alert("Error assigning task");
      }
  };

  const markComplete = async (id) => {
      if(window.confirm("Mark as Completed?")) {
          await updateDoc(doc(db, "tasks", id), { status: "Completed", completionDate: new Date().toISOString() });
      }
  };

  const handleSaveEdit = async () => {
      if(!editData) return;
      const updates = { ...editData };
      if(editData.status === 'Completed' && !editData.completionDate) updates.completionDate = new Date().toISOString();
      try {
          await updateDoc(doc(db, "tasks", editData.id), updates);
          alert("Updated ✅"); setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const handleDelete = async (id) => { if(window.confirm("Delete?")) await deleteDoc(doc(db, "tasks", id)); };

  const downloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filtered);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tasks");
    XLSX.writeFile(wb, "Tasks.xlsx");
  };

  const formatDate = (iso) => { if(!iso) return "-"; const d = new Date(iso); return isNaN(d) ? iso : d.toLocaleDateString('en-GB'); };

  const getRowColor = (dueDate, status) => {
      if (status === 'Completed') return 'inherit'; 
      const today = new Date().toISOString().split('T')[0];
      if (dueDate < today) return '#ffebee'; 
      if (dueDate === today) return '#fffde7'; 
      return 'inherit';
  };

  return (
    <div style={{padding:"10px", fontFamily:"Arial"}}>
      <div style={{marginBottom:"10px", textAlign:"right"}}>
          <button onClick={() => setShowAddTask(true)} style={btnAddTask}>➕ Assign Task</button>
      </div>

      <div style={headerStyle}>
        <h2 style={{margin:0, fontSize:"18px", color:"#8e44ad"}}>📋 Tasks ({filtered.length})</h2>
        <div style={{display:"flex", gap:"5px", flexWrap:"wrap"}}>
            <input type="text" placeholder="🔍 Search..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={{...inputStyle, width:"150px"}} />
            <button onClick={downloadExcel} style={btnDownload}>📥 Excel</button>
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto"}}>
          <table border="1" style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#8e44ad", color:"white"}}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>From ➝ To</th>
                    <th style={thStyle}>Dept</th>
                    <th style={thStyle}>Task</th>
                    <th style={thStyle}>Priority</th>
                    <th style={thStyle}>Due</th>
                    <th style={thStyle}>Status</th>
                    <th style={{...thStyle, width:"100px"}}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                 <tr key={t.id} style={{textAlign:"center", height:"35px", backgroundColor: getRowColor(t.date, t.status)}}>
                    <td style={tdStyle}>{formatDate(t.createdAt)}</td>
                    <td style={{...tdStyle, fontSize:"10px"}}>
                        <span style={{color:"gray"}}>{t.from}</span> ➝ <b>{t.to}</b>
                    </td>
                    <td style={tdStyle}>{t.department || '-'}</td>
                    <td style={{...tdStyle, textAlign:"left"}} title={t.task}>{t.task.substring(0,30)}...</td>
                    <td style={{...tdStyle, color: t.priority==='High'?'red':'green', fontWeight:"bold"}}>{t.priority}</td>
                    <td style={{...tdStyle, fontWeight: (t.date <= new Date().toISOString().split('T')[0] && t.status !== 'Completed') ? 'bold' : 'normal', color: (t.date < new Date().toISOString().split('T')[0] && t.status !== 'Completed') ? 'red' : 'inherit'}}>
                        {formatDate(t.date)}
                    </td>
                    <td style={{...tdStyle, fontWeight:"bold", color: t.status==='Completed'?'green':'orange'}}>{t.status}</td>
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"3px", justifyContent:"center"}}>
                            {t.status !== 'Completed' && <button onClick={()=>markComplete(t.id)} style={btnCheck} title="Mark Done">✔</button>}
                            <button onClick={() => setViewData(t)} style={btnIcon}>👁️</button>
                            {(isMaster || t.from === currentSender) && (
                                <>
                                    <button onClick={() => setEditData(t)} style={btnIconEdit}>✏️</button>
                                    <button onClick={() => handleDelete(t.id)} style={btnIconDelete}>🗑️</button>
                                </>
                            )}
                        </div>
                    </td>
                 </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ADD TASK MODAL */}
      {showAddTask && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#8e44ad", marginTop:0}}>➕ Assign Task</h3>
            <label style={labelStyle}>Assign From (You)</label>
            <input type="text" value={newTask.from} readOnly style={{...modalInput, backgroundColor:"#eee", cursor:"not-allowed"}}/>

            <div style={{display:"flex", gap:"5px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Assign To</label>
                    <select value={newTask.to} onChange={(e)=>setNewTask({...newTask, to:e.target.value})} style={modalInput}>
                        <option value="">Select User</option>
                        {/* ✅ FIX 3: Added '(users || []).map' to prevent crash in Modal */}
                        {(users || []).map((u, index) => (
                            <option key={index} value={u.name}>{u.name} ({u.role || 'Staff'})</option>
                        ))}
                    </select>
                </div>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Department</label>
                    <select value={newTask.department} onChange={(e)=>setNewTask({...newTask, department:e.target.value})} style={modalInput}>
                        <option value="">Select Dept</option>
                        <option value="Sales">Sales</option>
                        <option value="Service">Service</option>
                        <option value="Store">Store</option>
                        <option value="Account">Account</option>
                        <option value="HR">HR</option>
                        <option value="Admin">Admin</option>
                        <option value="Office">Office</option>
                        <option value="Other">Other</option>
                    </select>
                </div>
            </div>

            <label style={labelStyle}>Task Description</label>
            <textarea value={newTask.task} onChange={(e)=>setNewTask({...newTask, task:e.target.value})} style={{...modalInput, height:"60px"}}></textarea>
            
            <div style={{display:"flex", gap:"5px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Priority</label>
                    <select value={newTask.priority} onChange={(e)=>setNewTask({...newTask, priority:e.target.value})} style={modalInput}>
                        <option>High</option><option>Medium</option><option>Low</option>
                    </select>
                </div>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Due Date</label>
                    <input type="date" value={newTask.dueDate} onChange={(e)=>setNewTask({...newTask, dueDate:e.target.value})} style={modalInput}/>
                </div>
            </div>

            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=>setShowAddTask(false)} style={btnCancel}>Cancel</button>
                <button onClick={handleAddTask} style={btnSave}>Assign Task</button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW & EDIT MODALS */}
      {viewData && (
          <div style={modalOverlay}><div style={modalContent}><h3>Details</h3><pre>{JSON.stringify(viewData, null, 2)}</pre><button onClick={()=>setViewData(null)} style={btnClose}>Close</button></div></div>
      )}
      {editData && (
          <div style={modalOverlay}>
              <div style={modalContent}>
                <h3>Edit Task</h3>
                <label style={labelStyle}>Status</label>
                <select value={editData.status} onChange={(e)=>setEditData({...editData, status:e.target.value})} style={modalInput}>
                    <option>Pending</option><option>In Progress</option><option>Completed</option>
                </select>
                <label style={labelStyle}>Note/Remark</label>
                <textarea value={editData.completionNote || ""} onChange={(e)=>setEditData({...editData, completionNote:e.target.value})} style={modalInput} placeholder="Add a note..."></textarea>
                <div style={{marginTop:"15px", textAlign:"right"}}>
                    <button onClick={()=>setEditData(null)} style={btnCancel}>Cancel</button>
                    <button onClick={handleSaveEdit} style={btnSave}>Update</button>
                </div>
              </div>
          </div>
      )}
    </div>
  );
}

// STYLES
const headerStyle={backgroundColor:"#f8f9fa", padding:"8px", borderRadius:"8px", marginBottom:"10px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"11.5px", whiteSpace:"nowrap"};
const thStyle={padding:"8px 4px", border:"1px solid #ddd", backgroundColor:"#8e44ad", color:"white", textAlign:"center"};
const tdStyle={padding:"4px", border:"1px solid #ddd", verticalAlign:"middle"};
const inputStyle={padding:"5px", border:"1px solid #ccc", borderRadius:"4px", fontSize:"12px"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"5px 10px", borderRadius:"4px", cursor:"pointer", fontSize:"12px"};
const btnAddTask={backgroundColor:"#8e44ad", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold"};
const btnCheck={backgroundColor:"#27ae60", color:"white", border:"none", padding:"4px 6px", borderRadius:"3px", cursor:"pointer", fontSize:"12px"};
const btnIcon={backgroundColor:"#3498db", color:"white", border:"none", padding:"4px 6px", borderRadius:"3px", cursor:"pointer", fontSize:"12px"};
const btnIconEdit={backgroundColor:"#f39c12", color:"white", border:"none", padding:"4px 6px", borderRadius:"3px", cursor:"pointer", fontSize:"12px"};
const btnIconDelete={backgroundColor:"#e74c3c", color:"white", border:"none", padding:"4px 6px", borderRadius:"3px", cursor:"pointer", fontSize:"12px"};
const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"20px", borderRadius:"10px", width:"400px", boxShadow:"0 5px 15px rgba(0,0,0,0.3)"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};
const btnSave={backgroundColor:"#8e44ad", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

export default Tasks;